import { ref } from "vue";

import axios from "@/boot/axios";

import { handleApiError } from "@/utils/apiErrorUtils";

import { useAuthStore } from "@/stores/auth.store";
import { useAlertStore } from "@/stores/alert.store";

import { AxiosRequestConfig, type AxiosResponse } from "axios";

export const isLoading = ref(false);

export default class BaseService {
  static async get<T>(endpoint: string, params?: object): Promise<T | null> {
    try {
      isLoading.value = true;

      const response = await axios.get(endpoint, { params });
      if (response.status === 200) {
        return response.data || null;
      } else {
        console.error("GET request failed:", response.statusText);
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Re-lançar erros HTTP 400 para permitir tratamento customizado de erros detalhados
      if (error.response?.status === 400) {
        throw error;
      }

      if (
        error.response &&
        error.response.data &&
        error.response.data.StatusCode
      ) {
        handleApiError(error.response.data.StatusCode);
        if (error.response.data.StatusCode == 4) {
          useAuthStore().setLogout();
        }
      } else {
        useAlertStore().show("error", "Erro desconhecido");
      }

      console.error("Error in GET request:", error);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  static async post<T>(
    endpoint: string,
    data?: object,
    config?: AxiosRequestConfig
  ): Promise<T | null> {
    try {
      isLoading.value = true;

      const response = await axios.post(endpoint, data, config);
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        if (response.data.error === true) return response.data;

        return response.data || null;
      } else {
        console.error("POST request failed:", response.statusText);
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.StatusCode
      ) {
        handleApiError(error.response.data.StatusCode);
        if (error.response.data.statusCode == 4) {
          useAuthStore().setLogout();
        }
      } else {
        useAlertStore().show("error", "Erro desconhecido");
      }

      if (
        error.response &&
        error.response.data &&
        error.response.data.feedback &&
        error.response.data.feedback.suggestions
      ) {
        const suggestions = error.response.data.feedback.suggestions
        if (suggestions) {
          useAuthStore().setPasswordSuggestions(suggestions)
        }
      }

      console.error("Error in POST request:", error);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  static async put<T>(endpoint: string, data?: object): Promise<T | null> {
    try {
      isLoading.value = true;

      const response: AxiosResponse<T> = await axios.put(endpoint, data);
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        return response.data || null;
      } else {
        console.error("PUT request failed:", response.statusText);
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.StatusCode
      ) {
        handleApiError(error.response.data.StatusCode);
        if (error.response.data.statusCode == 4) {
          useAuthStore().setLogout();
        }
      } else {
        useAlertStore().show("error", "Erro desconhecido");
      }

      console.error("Error in PUT request:", error);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  static async delete<T>(endpoint: string, data?: object): Promise<T | null> {
    try {
      isLoading.value = true;

      const response: AxiosResponse<T> = await axios.delete(endpoint, {
        data: data,
      });

      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        return response.data || null;
      } else {
        console.error("DELETE request failed:", response.statusText);
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.StatusCode
      ) {
        handleApiError(error.response.data.StatusCode);
        if (error.response.data.statusCode == 4) {
          useAuthStore().setLogout();
        }
      } else {
        useAlertStore().show("error", "Erro desconhecido");
      }

      console.error("Error in DELETE request:", error);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  static async patch<T>(endpoint: string, data?: object): Promise<T | null> {
    try {
      isLoading.value = true;

      const response: AxiosResponse<T> = await axios.patch(endpoint, data);
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        return response.data || null;
      } else {
        console.error("PATCH request failed:", response.statusText);
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.StatusCode
      ) {
        handleApiError(error.response.data.StatusCode);
        if (error.response.data.statusCode == 4) {
          useAuthStore().setLogout();
        }
      } else {
        useAlertStore().show("error", "Erro desconhecido");
      }

      console.error("Error in PATCH request:", error);
      return null;
    } finally {
      isLoading.value = false;
    }
  }
}
